import { useMemo, useState } from "react";
import { ClipboardCheck, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useNotification } from "@/shared/hooks/useNotification";
import { normalizeError } from "@/shared/errors/normalizeError";
import { useGreenhouseBeds, useGreenhouses } from "../hooks/useGreenhouses";
import { useGreenhouseStockTake, useSaveGreenhouseStockTake } from "../hooks/useGreenhouseStockTake";
import type { StockTakeRow, StockTakeStatus } from "../services/greenhouseStockTakeApi";

const statusMeta: Record<StockTakeStatus, { label: string; className: string }> = {
  NoBeds: { label: "No bed location", className: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200" },
  Over: { label: "Beds over", className: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  Under: { label: "Beds under", className: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
  Match: { label: "Matches", className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
};

export const GreenhouseStockTakePage = () => {
  const [greenhouseId, setGreenhouseId] = useState("");
  const [status, setStatus] = useState<StockTakeStatus | "">("");
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState<StockTakeRow | null>(null);
  const debouncedSearch = useDebounce(search, 350);
  const greenhousesQuery = useGreenhouses();
  const query = useGreenhouseStockTake({
    greenhouseId: greenhouseId || undefined,
    status: status || undefined,
    search: debouncedSearch || undefined,
    pageNumber,
    pageSize,
  });

  if (query.isLoading) return <LoadingAnimation fullScreen />;
  if (query.isError || !query.data) {
    return <Card><CardContent className="p-6 text-sm text-destructive">Unable to load the stock-take.</CardContent></Card>;
  }

  const { summary, items } = query.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Growing operations</p>
          <h1 className="text-3xl font-bold tracking-tight">Greenhouse Stock-Take</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Record the trays physically counted on each bed. System trays come from the stock ledger and are not changed here.
          </p>
        </div>
        <Button variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <SummaryCard title="Batches" value={summary.batches} hint={`${summary.systemTrays} system / ${summary.bedTrays} on beds`} />
        <SummaryCard title="No bed location" value={summary.noBeds} onClick={() => { setStatus("NoBeds"); setPageNumber(1); }} />
        <SummaryCard title="Beds over" value={summary.over} onClick={() => { setStatus("Over"); setPageNumber(1); }} />
        <SummaryCard title="Beds under" value={summary.under} onClick={() => { setStatus("Under"); setPageNumber(1); }} />
        <SummaryCard title="Matches" value={summary.match} onClick={() => { setStatus("Match"); setPageNumber(1); }} />
      </div>

      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-3">
          <label className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" value={search} onChange={(e) => { setSearch(e.target.value); setPageNumber(1); }} placeholder="Search crop, variety, instruction or bed" />
          </label>
          <Select value={greenhouseId || "all"} onValueChange={(v) => { setGreenhouseId(v === "all" ? "" : v); setPageNumber(1); }}>
            <SelectTrigger><SelectValue placeholder="All greenhouses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All greenhouses</SelectItem>
              {(greenhousesQuery.data ?? []).map((g) => <SelectItem key={g.id} value={g.id}>{g.greenhouseName}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status || "all"} onValueChange={(v) => { setStatus((v === "all" ? "" : v) as StockTakeStatus | ""); setPageNumber(1); }}>
            <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(Object.keys(statusMeta) as StockTakeStatus[]).map((s) => <SelectItem key={s} value={s}>{statusMeta[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch</TableHead>
                <TableHead>Greenhouse</TableHead>
                <TableHead className="text-right">System trays</TableHead>
                <TableHead className="text-right">On beds</TableHead>
                <TableHead className="text-right">Difference</TableHead>
                <TableHead>Beds</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.stockEntryId}>
                  <TableCell>
                    <p className="font-medium">{row.cropType}{row.variety ? ` · ${row.variety}` : ""}</p>
                    <p className="text-xs text-muted-foreground">{row.instructionNumber || "Standalone stock"}{row.trayType ? ` · ${row.trayType}` : ""}</p>
                  </TableCell>
                  <TableCell>{row.greenhouseName ?? "—"}</TableCell>
                  <TableCell className="text-right" title={`Total ${row.totalQuantity} + reserve ${row.fillingReserve} − dispatched ${row.dispatchedQuantity}`}>
                    {row.systemTrays}
                  </TableCell>
                  <TableCell className="text-right">{row.bedTrays}</TableCell>
                  <TableCell className={`text-right ${row.difference === 0 ? "" : "font-semibold text-destructive"}`}>
                    {row.difference > 0 ? `+${row.difference}` : row.difference}
                  </TableCell>
                  <TableCell className="max-w-64 text-xs text-muted-foreground">
                    {row.beds.length === 0 ? "—" : row.beds.map((b) => `${b.bayName} / ${b.bedNumber}: ${b.trayCount}`).join(", ")}
                  </TableCell>
                  <TableCell><Badge variant="secondary" className={statusMeta[row.status].className}>{statusMeta[row.status].label}</Badge></TableCell>
                  <TableCell>
                    <PermissionGate permissions={["greenhouse_stock.manage"]}>
                      <Button size="sm" variant="outline" onClick={() => setEditing(row)}>
                        <ClipboardCheck className="mr-1 h-4 w-4" /> Record count
                      </Button>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="p-8 text-center text-sm text-muted-foreground">No stock batches match the selected filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PaginationBar
        currentPage={query.data.pageNumber}
        pageSize={query.data.pageSize}
        totalCount={query.data.totalCount}
        totalPages={query.data.totalPages}
        onPageChange={setPageNumber}
        onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
      />

      {editing && <StockTakeDialog row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
};

const SummaryCard = ({ title, value, hint, onClick }: { title: string; value: number; hint?: string; onClick?: () => void }) => (
  <Card className={onClick ? "cursor-pointer hover:bg-muted/40" : undefined} onClick={onClick}>
    <CardContent className="p-4">
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </CardContent>
  </Card>
);

type DraftBed = { greenhouseBedId: string; label: string; trayCount: number; minimum: number };

const StockTakeDialog = ({ row, onClose }: { row: StockTakeRow; onClose: () => void }) => {
  const notify = useNotification();
  const save = useSaveGreenhouseStockTake();
  const greenhousesQuery = useGreenhouses();
  const [greenhouseId, setGreenhouseId] = useState(row.greenhouseId ?? "");
  const [beds, setBeds] = useState<DraftBed[]>(() => row.beds.map((b) => ({
    greenhouseBedId: b.greenhouseBedId,
    label: `${b.bayName} / ${b.bedNumber}`,
    trayCount: b.trayCount,
    minimum: b.pendingHandoverTrays,
  })));
  const [bedToAdd, setBedToAdd] = useState("");
  const [notes, setNotes] = useState("");
  const bedsQuery = useGreenhouseBeds(greenhouseId);

  const addableBeds = useMemo(
    () => (bedsQuery.data ?? []).filter((b) => b.isActive && !beds.some((d) => d.greenhouseBedId === b.id)),
    [bedsQuery.data, beds]
  );
  const counted = beds.reduce((sum, b) => sum + b.trayCount, 0);
  const difference = counted - row.systemTrays;
  const belowMinimum = beds.some((b) => b.trayCount < b.minimum);

  const setCount = (bedId: string, value: string) =>
    setBeds((current) => current.map((b) => b.greenhouseBedId === bedId
      ? { ...b, trayCount: Math.max(0, parseInt(value || "0", 10) || 0) }
      : b));

  const addBed = () => {
    const bed = addableBeds.find((b) => b.id === bedToAdd);
    if (!bed) return;
    setBeds((current) => [...current, { greenhouseBedId: bed.id, label: `${bed.bayName} / ${bed.bedNumber}`, trayCount: 0, minimum: 0 }]);
    setBedToAdd("");
  };

  const changeGreenhouse = (id: string) => {
    setGreenhouseId(id);
    setBedToAdd("");
    // Beds must all sit in one greenhouse, so trays on the previous greenhouse's beds are cleared.
    setBeds((current) => current.map((b) => ({ ...b, trayCount: 0 })));
  };

  const handleSave = async () => {
    try {
      await save.mutateAsync({
        stockEntryId: row.stockEntryId,
        payload: {
          beds: beds.map((b) => ({ greenhouseBedId: b.greenhouseBedId, trayCount: b.trayCount })),
          notes: notes.trim() || undefined,
        },
      });
      notify.success("Stock-take saved");
      onClose();
    } catch (err) {
      notify.error(normalizeError(err).userMessage || "Failed to save stock-take");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Stock-take — {row.cropType}{row.variety ? ` · ${row.variety}` : ""}</DialogTitle>
          <DialogDescription>
            {row.instructionNumber || "Standalone stock"}{row.trayType ? ` · ${row.trayType}` : ""}. System expects{" "}
            <strong>{row.systemTrays}</strong> trays (total {row.totalQuantity} + reserve {row.fillingReserve} − dispatched {row.dispatchedQuantity}).
            Enter what is physically on each bed; beds have no size limit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Greenhouse</Label>
            <Select value={greenhouseId} onValueChange={changeGreenhouse}>
              <SelectTrigger><SelectValue placeholder="Select greenhouse" /></SelectTrigger>
              <SelectContent>
                {(greenhousesQuery.data ?? []).map((g) => <SelectItem key={g.id} value={g.id}>{g.greenhouseName}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Trays per bed</Label>
            {beds.length === 0 && <p className="text-sm text-muted-foreground">No beds recorded yet. Add the beds this batch is on.</p>}
            {beds.map((bed) => (
              <div key={bed.greenhouseBedId} className="grid grid-cols-[1fr_110px_36px] items-center gap-2">
                <div className="text-sm">
                  {bed.label}
                  {bed.minimum > 0 && <span className="ml-2 text-xs text-amber-700">{bed.minimum} held for pending handover</span>}
                </div>
                <Input
                  type="number"
                  min={bed.minimum}
                  step={1}
                  value={bed.trayCount}
                  onChange={(e) => setCount(bed.greenhouseBedId, e.target.value)}
                  className={bed.trayCount < bed.minimum ? "border-destructive" : undefined}
                />
                <Button type="button" size="icon" variant="ghost" onClick={() => setCount(bed.greenhouseBedId, "0")} title="Set to zero">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div className="flex gap-2">
              <Select value={bedToAdd} onValueChange={setBedToAdd} disabled={!greenhouseId || bedsQuery.isLoading}>
                <SelectTrigger className="flex-1"><SelectValue placeholder={bedsQuery.isLoading ? "Loading beds…" : "Add a bed…"} /></SelectTrigger>
                <SelectContent>
                  {addableBeds.map((b) => <SelectItem key={b.id} value={b.id}>{b.bayName} / {b.bedNumber}{b.currentOccupancy > 0 ? ` (${b.currentOccupancy} trays on bed)` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={addBed} disabled={!bedToAdd}>
                <Plus className="mr-1 h-4 w-4" /> Add
              </Button>
            </div>
          </div>

          <p className={`text-sm ${difference === 0 ? "text-muted-foreground" : "text-amber-700"}`}>
            Counted {counted} / system {row.systemTrays} trays
            {difference > 0 && ` — ${difference} more than the ledger`}
            {difference < 0 && ` — ${-difference} fewer than the ledger`}
            {difference !== 0 && ". If the count is right, raise a discard or reserve adjustment for the difference."}
          </p>

          <div className="space-y-1">
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Counted with supervisor on 30/09" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={save.isPending || belowMinimum}>
            {save.isPending ? "Saving…" : "Save stock-take"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
